import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Dropdown, DropdownContent, InputRadio } from 'shared/ui'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { Label } from 'shared/ui/Label'
import { type RegisterStore } from 'pages/register/ui/Register'
import { DropdownCrmContent } from './DropdownCrmContent'

import styles from './styles.module.scss'

type IFormProps = {
  store: RegisterStore
}

export const Step2Form = observer(({ store }: IFormProps) => {
  const selectRef = useRef<HTMLLabelElement>(null)

  const [selectWidth, setSelectWidth] = useState<number>()
  const [selectHeight, setSelectHeight] = useState<number>()
  const {
    step2Store: { crm, contactsCount, contactsCounts, setContactsCount, isNextDisabled },
    handleNextStep,
  } = store

  const updateDimensions = useCallback(() => {
    if (selectRef.current) {
      setSelectWidth(selectRef.current.clientWidth)
      setSelectHeight(selectRef.current.clientHeight)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('resize', updateDimensions)
    updateDimensions()

    return () => {
      window.removeEventListener('resize', updateDimensions)
    }
  }, [])

  return (
    <div className={styles.wrap}>
      <Label
        marginTop={0}
        label={{
          text: 'Which CRM does your company use?',
          className: styles.inputLabel,
        }}
      />
      <Dropdown
        placement={'bottom-start'}
        width={selectWidth}
        margin={selectHeight ? -selectHeight : undefined}
        triggerComponent={() => (
          <TextFieldPlaceholder
            ref={selectRef}
            rightActions={[
              makeTextFieldIcon({
                icon: 'chevronDown',
              }),
            ]}
            placeholder={
              crm ? (
                <Button
                  className={styles.placeholderItem}
                  typeBtn='menuItem'
                  type='button'
                  icon={crm.icon}
                  text={crm.name}
                  fullWidth
                />
              ) : (
                'Select'
              )
            }
            variant={'stroke'}
            size={'small'}
            className={classNames(styles.textField, {
              [styles.placeholder]: !crm,
            })}
          />
        )}
        customComponent={(onClose) => (
          <DropdownContent noPadding>
            <DropdownCrmContent store={store} onClose={onClose} />
          </DropdownContent>
        )}
      />

      <Label
        label={{
          text: 'How many contacts do you have?',
          className: styles.inputLabel,
        }}
      />
      <div className={styles.wrapSizes}>
        {contactsCounts.map((count) => (
          <InputRadio
            className={classNames(styles.inputSize, {
              [styles.active]: count === contactsCount,
            })}
            checked={count === contactsCount}
            onChecked={() => setContactsCount(count)}
            key={count}
            label={count}
          />
        ))}
      </div>
      <Button
        className={styles.button}
        typeBtn='contained'
        type='button'
        onClick={handleNextStep}
        disabled={isNextDisabled}
        text={'Next'}
        contained={'primary'}
        fullWidth
      />
    </div>
  )
})

export default Step2Form
